/**
 * Sticky top-bar across the admin SPA showing pending preview changes
 * for clubs with features.preview enabled.
 *
 *   "3 pending changes — [Open preview ↗] [Publish all] [Discard]"
 *
 * Polls /admin/preview/status on mount + after every Publish/Discard
 * action. Self-hides when count is 0 and no settings draft (i.e. nothing
 * to act on). Hides entirely for clubs without features.preview.
 *
 * Server-side gates the same way — Phase D will add the lambda 402 for
 * non-eligible plans; this bar already self-hides without it.
 */
import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  getPreviewStatus,
  publishAllPreview,
  discardAllPreview,
  getPreviewToken,
} from '../api/client';
import './PreviewBar.css';

export default function PreviewBar() {
  const { features, clubId, plan } = useAuth();
  // Mirror the server gate: features.preview AND plan ∈ {club, pro}.
  // Free clubs never see the bar even if the flag is on by accident.
  const enabled = features?.preview === true && (plan === 'club' || plan === 'pro');

  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const refresh = async () => {
    if (!enabled) return;
    try {
      const s = await getPreviewStatus();
      setCount(s.count);
    } catch {
      // transient — don't surface; the bar just stays hidden
    }
  };

  useEffect(() => {
    refresh();
    if (!enabled) return;
    // Four triggers so the bar reflects reality without operator refresh:
    // (a) a periodic poll every 20s WHILE THE TAB IS VISIBLE, (b) tab
    // refocus, (c) becoming visible again, (d) pages that mutate content
    // dispatch a window 'preview-changed' event to force an immediate
    // refresh.
    //
    // The visibility guard is not a micro-optimisation. A backgrounded tab
    // keeps running this interval — throttled by the browser to 60s — so one
    // admin who left the tab open sent 1,452 requests in 24 hours, more than
    // every club's public read traffic put together (measured 2026-09-07).
    // A hidden tab is showing nobody a stale count, so it has no reason to
    // ask; the visibility listener makes it current the moment it is looked
    // at, which is sooner than the 20s poll managed anyway.
    const interval = window.setInterval(() => {
      if (document.hidden) return;
      refresh();
    }, 20_000);
    const onFocus = () => refresh();
    const onChanged = () => refresh();
    const onVisible = () => { if (!document.hidden) refresh(); };
    window.addEventListener('focus', onFocus);
    window.addEventListener('preview-changed', onChanged);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('preview-changed', onChanged);
      document.removeEventListener('visibilitychange', onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  if (!enabled || count === 0) return null;

  // The preview site only shows drafts to a token, carried in the link's
  // fragment so it never reaches a server log or a Referer header. The tab
  // opens inside the click so popup blockers allow it, then gets the link.
  const openPreview = async () => {
    if (!clubId) return;
    const tab = window.open('', '_blank');
    if (tab) tab.opener = null;
    setError('');
    try {
      const { token } = await getPreviewToken();
      const url = `https://${clubId}-preview.grassrootsfc.co.uk/#preview=${token}`;
      if (tab) tab.location.href = url;
      else window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err: any) {
      tab?.close();
      setError(err?.message || 'Could not open preview');
    }
  };

  const onPublish = async () => {
    if (!window.confirm(`Publish ${count} pending change${count === 1 ? '' : 's'} to your live site?`)) return;
    setLoading(true); setError('');
    try {
      const res = await publishAllPreview();
      console.log('[Preview] published:', res);
      await refresh();
      // Tell other open admin pages (e.g. ContentEditor) to reload
      // their data so they don't show the now-published draft as
      // "saved but unpublished" any more.
      window.dispatchEvent(new Event('preview-published'));
    } catch (err: any) {
      setError(err?.message || 'Publish failed');
    } finally {
      setLoading(false);
    }
  };

  const onDiscard = async () => {
    if (!window.confirm(`Discard all ${count} pending change${count === 1 ? '' : 's'}? This can't be undone.`)) return;
    setLoading(true); setError('');
    try {
      const res = await discardAllPreview();
      console.log('[Preview] discarded:', res);
      await refresh();
      window.dispatchEvent(new Event('preview-discarded'));
    } catch (err: any) {
      setError(err?.message || 'Discard failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="preview-bar" role="status">
      <span className="pb-dot" aria-hidden />
      <span className="pb-text">
        <strong>{count}</strong> pending change{count === 1 ? '' : 's'}
        <span className="pb-sub"> — not visible on your live site</span>
      </span>
      <div className="pb-actions">
        <button type="button" className="pb-btn pb-btn-ghost" onClick={openPreview}>
          Open preview ↗
        </button>
        <button type="button" className="pb-btn pb-btn-primary" onClick={onPublish} disabled={loading}>
          {loading ? 'Working…' : 'Publish all'}
        </button>
        <button type="button" className="pb-btn pb-btn-link" onClick={onDiscard} disabled={loading}>
          Discard
        </button>
      </div>
      {error && <span className="pb-error">{error}</span>}
    </div>
  );
}
